import { useState } from "react";
import { Mail, Phone, Send, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContattiPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    privacy_accepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    "Ristorazione",
    "Retail & Attività Commerciali",
    "Gestione Prenotazioni",
    "Magazzino & Forniture",
    "Gestione Lavanderia",
    "Società Sportive Dilettantistiche",
    "Gestione Risorse Umane",
    "Promozioni Commerciali",
    "Altro"
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacy_accepted) {
      toast.error("Devi accettare la privacy policy per inviare il messaggio");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Messaggio inviato con successo!");
      setFormData({
        name: "",
        company: "",
        email: "",
        phone: "",
        service: "",
        message: "",
        privacy_accepted: false
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Errore durante l'invio. Riprova più tardi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "info@isiap.it",
      link: "mailto:info@isiap.it"
    },
    {
      icon: Phone,
      title: "Telefono",
      value: "0383 644311",
      link: "tel:0383644311"
    },
    {
      icon: Phone,
      title: "Cellulare",
      value: "351 752 1168",
      link: "tel:3517521168"
    }
  ];

  return (
    <div data-testid="contatti-page">
      {/* Hero Section */}
      <section className="bg-[#f5f6fa] py-16 md:py-24" data-testid="contact-hero">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge mb-6">Contatti</span>
            <h1 className="heading-1 text-[#1a1f36] mb-6">
              Scopri come possiamo aiutarti
            </h1>
            <p className="body-large">
              Hai domande su iSiAP? Vuoi richiedere una demo? 
              Compila il form o contattaci direttamente.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding" data-testid="contact-form-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="heading-3 text-[#1a1f36] mb-4">Informazioni di Contatto</h2>
                <p className="text-[#4a5068]">
                  Il nostro team è disponibile per rispondere a tutte le tue domande.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info) => (
                  <a
                    key={info.title}
                    href={info.link}
                    className="flex items-start gap-4 p-4 rounded-xl bg-[#f5f6fa] hover:bg-[#FF6B00]/10 transition-colors group border border-[#e2e4ed]"
                    data-testid={`contact-info-${info.title.toLowerCase()}`}
                  >
                    <div className="icon-container group-hover:bg-[#FF6B00] group-hover:text-white transition-colors">
                      <info.icon size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-[#7a7f94] mb-1">{info.title}</p>
                      <p className="font-semibold text-[#1a1f36]">{info.value}</p>
                    </div>
                  </a>
                ))}
              </div>

              <div className="p-6 rounded-xl bg-[#1a1f36] text-white">
                <h3 className="font-semibold mb-3 text-[#FF6B00]">Orari di Assistenza</h3>
                <div className="space-y-2 text-sm text-[#9ca3af]">
                  <p>Lunedì - Venerdì: 9:00 - 18:00</p>
                  <p>Sabato: 9:00 - 13:00</p>
                  <p>Assistenza tecnica 24/7</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-[#e2e4ed] p-8 md:p-12 shadow-sm">
                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="form-success">
                    <div className="w-20 h-20 bg-[#FF6B00]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 size={40} className="text-[#FF6B00]" />
                    </div>
                    <h3 className="heading-3 text-[#1a1f36] mb-4">Messaggio Inviato!</h3>
                    <p className="text-[#4a5068] mb-8">
                      Grazie per averci contattato. Ti risponderemo il prima possibile.
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="btn-secondary"
                      data-testid="send-another-btn"
                    >
                      Invia un altro messaggio
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-[#1a1f36] mb-2">
                          Nome e Cognome *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="input-style"
                          placeholder="Mario Rossi"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-semibold text-[#1a1f36] mb-2">
                          Azienda
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="input-style"
                          placeholder="Nome azienda"
                          data-testid="input-company"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-[#1a1f36] mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="input-style"
                          placeholder="mario@esempio.it"
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-[#1a1f36] mb-2">
                          Telefono
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="input-style"
                          placeholder="+39 333 1234567"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-semibold text-[#1a1f36] mb-2">
                        Servizio di Interesse
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="input-style"
                        data-testid="select-service"
                      >
                        <option value="">Seleziona un servizio</option>
                        {services.map((service) => (
                          <option key={service} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-[#1a1f36] mb-2">
                        Messaggio *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="textarea-style"
                        placeholder="Come possiamo aiutarti?"
                        rows={5}
                        data-testid="input-message"
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="privacy_accepted"
                        name="privacy_accepted"
                        checked={formData.privacy_accepted}
                        onChange={handleChange}
                        className="mt-1 w-4 h-4 text-[#FF6B00] border-[#e2e4ed] rounded focus:ring-[#FF6B00] accent-[#FF6B00]"
                        data-testid="checkbox-privacy"
                      />
                      <label htmlFor="privacy_accepted" className="text-sm text-[#4a5068]">
                        Dichiaro di aver letto e accettato i{" "}
                        <a href="#" className="text-[#FF6B00] underline">Termini e Condizioni</a>
                        {" "}e l'{" "}
                        <a href="#" className="text-[#FF6B00] underline">Informativa sulla Privacy</a>.
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full md:w-auto"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Invio in corso...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Invia Messaggio
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Navy */}
      <section className="bg-[#1a1f36] section-padding" data-testid="location-section">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              iSiAP - Un prodotto 4D Sistemi Informatici
            </h2>
            <p className="text-[#9ca3af] text-lg mb-12">
              Con oltre 15 anni di esperienza nel settore, siamo il partner ideale 
              per la digitalizzazione della tua attività commerciale.
            </p>
            <div className="flex flex-wrap justify-center gap-12">
              <div className="text-center">
                <p className="text-5xl font-bold text-[#FF6B00] mb-2">900+</p>
                <p className="text-[#9ca3af]">Punti Vendita</p>
              </div>
              <div className="text-center">
                <p className="text-5xl font-bold text-[#FF6B00] mb-2">15+</p>
                <p className="text-[#9ca3af]">Anni Esperienza</p>
              </div>
              <div className="text-center">
                <p className="text-5xl font-bold text-[#FF6B00] mb-2">24/7</p>
                <p className="text-[#9ca3af]">Supporto Tecnico</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContattiPage;
